import { Component, OnDestroy, OnInit, Input, OnChanges, Output, EventEmitter, SimpleChanges } from '@angular/core';
import { DropdownModule } from 'primeng/dropdown';
import { CrudOrgFacade, CrudOrgMode } from './crud-org.facade';
import { Subscription } from 'rxjs';
import { CountryModel } from '../../global-data-models/country.model';
import { COMMON_IMPORTS } from '../../global-misc/common-imports';
import { FormGroup } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { TimezoneModel } from '../../global-data-models/timezone.model';

import { REFRACTIVE_INSTRUMENTS, ASTIGMATISM_INSTRUMENTS, CATARACT_INSTRUMENTS } from "../../global-data-models/org.model"

@Component({
  selector: 'app-crud-org',
  imports: [...COMMON_IMPORTS, DropdownModule, TranslateModule],
  templateUrl: './crud-org.component.html',
  styleUrls: ['./crud-org.component.css']
})
export class CrudOrgComponent implements OnInit, OnDestroy, OnChanges {
  @Input() mode: CrudOrgMode = 'create';
  @Input() orgId?: string;

  /** Whether or not to style the component for onboarding or not */
  @Input() styleForOnboarding!: boolean;

  @Output() cancelPressed = new EventEmitter<void>();
  @Output() UpdateSuccess = new EventEmitter<void>();

  public form: FormGroup = this.facade.form;

  public page: number = 1; /* reset to 1 when done */

  public countries: CountryModel[] = [];
  public timezones: TimezoneModel[] = [];

  private sub = new Subscription();

  constructor(private facade: CrudOrgFacade) { }

  ngOnInit(): void {
    this.facade.begin(this.mode, this.orgId);

    this.sub.add(
      this.facade.state$.subscribe((state) => {
        this.form = state.form;
        this.countries = state.countries;
        this.timezones = state.timezones;
      }),
    );
  }

  public shouldShowFieldError(page: string, field: string): boolean {
    const formField = this.form.get(page)!.get(field);
    return formField!.invalid && formField!.touched;
  }

  public getRefractiveInstruments(): string[] { return REFRACTIVE_INSTRUMENTS; }
  public getAstigmatismInstruments(): string[] { return ASTIGMATISM_INSTRUMENTS; }
  public getCataractInstruments(): string[] { return CATARACT_INSTRUMENTS; }

  public getRefractiveInstrumentsWithOther(): { label: string, value: string }[] {
    const instruments = this.getRefractiveInstruments().map(instrument => ({ label: instrument, value: instrument }));
    return [...instruments, { label: 'Other', value: 'OTHER' }];
  }

  public getAstigmatismInstrumentsWithOther(): { label: string, value: string }[] {
    const instruments = this.getAstigmatismInstruments().map(instrument => ({ label: instrument, value: instrument }));
    return [...instruments, { label: 'Other', value: 'OTHER' }];
  }

  public getCataractInstrumentsWithOther(): { label: string, value: string }[] {
    const instruments = this.getCataractInstruments().map(instrument => ({ label: instrument, value: instrument }));
    return [...instruments, { label: 'Other', value: 'OTHER' }];
  }

  public isRefractiveInstrumentOther(): boolean {
    return this.getPageFormGroup(2).get('refractiveInstrument')?.value === 'OTHER';
  }

  public isAstigmatismInstrumentOther(): boolean {
    return this.getPageFormGroup(2).get('astigmatismInstrument')?.value === 'OTHER';
  }

  public isCataractInstrumentOther(): boolean {
    return this.getPageFormGroup(2).get('cataractInstrument')?.value === 'OTHER';
  }

  public onRefractiveInstrumentChange(event: any): void {
    const formGroup = this.getPageFormGroup(2);
    if (event.value !== 'OTHER') {
      formGroup.get('refractiveInstrumentOther')?.setValue('');
    }
  }

  public onAstigmatismInstrumentChange(event: any): void {
    const formGroup = this.getPageFormGroup(2);
    if (event.value !== 'OTHER') {
      formGroup.get('astigmatismInstrumentOther')?.setValue('');
    }
  }

  public onCataractInstrumentChange(event: any): void {
    const formGroup = this.getPageFormGroup(2);
    if (event.value !== 'OTHER') {
      formGroup.get('cataractInstrumentOther')?.setValue('');
    }
  }

  public getPageFormGroup(page: number): FormGroup {
    return this.form.get(`page${page}`) as FormGroup;
  }
  public nextPage(): void { ++this.page; }
  public prevPage(): void { --this.page; }

  public isCurrentPageInvalid(): boolean {
    const currentPageForm = this.form.get(`page${this.page}`) as FormGroup;
    const invalid: boolean = !!(currentPageForm?.invalid);
    return invalid;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['mode'] && !changes['mode'].firstChange) {
      this.facade.begin(this.mode, this.orgId);
    }
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  async onSave() {
    const success = await this.facade.saveButtonPressed();
    if (success && this.mode === 'update') {
      this.UpdateSuccess.emit();
    }
  }

  onCancel() {
    this.cancelPressed.emit();
  }
}
